import React, { useEffect, useState } from 'react';
import { eventsAPI } from '../services/api';
import { Event } from '../types';
import EventCard from '../components/EventCard';
import { format, parseISO } from 'date-fns';

const filterOptions = [
  { label: 'All', value: 'all' },
  { label: 'Keynote', value: 'keynote' },
  { label: 'Distinguished Lecture', value: 'distinguished-lecture' },
  { label: 'Workshop', value: 'workshop' },
];

const EventsPage: React.FC = () => {
  const [thisWeekEvents, setThisWeekEvents] = useState<Event[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'this-week' | 'upcoming'>('this-week');
  const [filter, setFilter] = useState<string>('all');

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      const thisWeekData = await eventsAPI.getThisWeekEvents();
      setThisWeekEvents(thisWeekData.events || []);

      const upcomingData = await eventsAPI.getUpcomingEvents(20);
      setUpcomingEvents(upcomingData.events || []);
    } catch (err) {
      console.error('Error loading events:', err);
      setError('Failed to load events. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Filtered events
  const filteredThisWeekEvents =
    filter === 'all'
      ? thisWeekEvents
      : thisWeekEvents.filter(
          (event) => event.tags?.includes(filter) || event.event_type === filter
        );

  const filteredUpcomingEvents =
    filter === 'all'
      ? upcomingEvents
      : upcomingEvents.filter(
          (event) => event.tags?.includes(filter) || event.event_type === filter
        );

  const keynoteEvents = filteredUpcomingEvents.filter(
    (event) =>
      event.tags?.includes('keynote') ||
      event.tags?.includes('distinguished-lecture') ||
      event.event_type === 'distinguished-lecture'
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Campus Events</h1>
          <p className="text-gray-600">
            Stay connected with CMU-Africa's vibrant campus community
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm mb-4 p-1 flex space-x-1">
          <button
            onClick={() => setActiveTab('this-week')}
            className={`flex-1 py-3 px-6 rounded-lg font-semibold transition ${
              activeTab === 'this-week'
                ? 'bg-[#C41230] text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            📅 This Week ({thisWeekEvents.length})
          </button>
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`flex-1 py-3 px-6 rounded-lg font-semibold transition ${
              activeTab === 'upcoming'
                ? 'bg-[#C41230] text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            🔮 Upcoming ({upcomingEvents.length})
          </button>
        </div>

        {/* Filter Options */}
        <div className="mb-6 flex flex-wrap gap-2">
          {filterOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setFilter(option.value)}
              className={`px-4 py-2 rounded-lg font-semibold transition ${
                filter === option.value
                  ? 'bg-[#C41230] text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {/* Loading & Error */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#C41230] border-t-transparent"></div>
            <p className="mt-4 text-gray-600">Loading events...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-600 font-semibold">{error}</p>
            <button
              onClick={loadEvents}
              className="mt-4 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            {/* This Week Tab */}
            {activeTab === 'this-week' && (
              <div>
                {filteredThisWeekEvents.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredThisWeekEvents.map((event) => (
                      <EventCard key={event.id} event={event} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-white rounded-lg shadow-sm">
                    <p className="text-gray-500 text-lg">No events scheduled for this week</p>
                  </div>
                )}
              </div>
            )}

            {/* Upcoming Tab */}
            {activeTab === 'upcoming' && (
              <div>
                {keynoteEvents.length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                      <span className="mr-2">🎤</span>
                      Keynote & Distinguished Lectures
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {keynoteEvents.map((event) => (
                        <EventCard key={event.id} event={event} featured />
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">All Upcoming Events</h2>
                  {filteredUpcomingEvents.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {filteredUpcomingEvents.map((event) => (
                        <EventCard key={event.id} event={event} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-white rounded-lg shadow-sm">
                      <p className="text-gray-500 text-lg">No upcoming events</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EventsPage;
